import CloseIcon from '@mui/icons-material/Close';
import {
  Chip,
  Box,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  chipClasses,
  styled,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { WsColors, WsDialog, WsTypography, WsTypoVariants } from 'wisestep-base';

import { StyledLink } from './ExpandableTypography';

export const ChipStyle = styled(Chip)(() => ({
  borderRadius: 4,
  color: WsColors.TheBlues700,
  padding: 0,
  fontSize: 12,
  fontWeight: 400,
  border: `1px solid ${WsColors.TheBlues600}`,
  backgroundColor: WsColors.TheBlue75,
  [`& .${chipClasses.label}`]: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const Container = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  overflow: 'hidden',
  flexWrap: 'nowrap',
  width: '100%',
}));

const ExpandableChips = ({ items, label }: { items: string[]; label: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const maxChipSize = useRef<number>(65);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current;
      if (!container) return;

      const chipElements = Array.from(container.children);

      maxChipSize.current = container?.offsetWidth / 1.4;

      let totalWidth = 0;
      let count = 0;

      for (const chip of chipElements) {
        const width = (chip as HTMLElement).offsetWidth + 8;
        if (totalWidth + width > container.offsetWidth) break;
        totalWidth += width;
        count++;
      }
      setVisibleCount(count < items.length ? count - 1 : items.length);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [items]);

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  return (
    <>
      <Container ref={containerRef}>
        {items.slice(0, visibleCount).map((item, index) => (
          <ChipStyle key={index} label={item} sx={{ maxWidth: maxChipSize.current }} />
        ))}

        {visibleCount < items.length && (
          <StyledLink underline="none" variant="body1" onClick={handleOpenDialog}>
            {`+${items.length - visibleCount} more`}
          </StyledLink>
        )}
        {items.slice(visibleCount).map((item, index) => (
          <ChipStyle key={index} label={item} sx={{ maxWidth: 65, visibility: 'hidden' }} />
        ))}
      </Container>

      <WsDialog openDialog={open} onClose={handleCloseDialog} size="small">
        <DialogTitle>
          <Grid container direction="row" justifyContent="space-between">
            <Grid item>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                More {label}
              </WsTypography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleCloseDialog} sx={{ padding: 0 }}>
                <CloseIcon sx={{ fill: WsColors.GreyBlack, cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container gap={1} flexWrap="wrap">
            {items.map((item, index) => (
              <ChipStyle key={index} label={item} />
            ))}
          </Grid>
        </DialogContent>
      </WsDialog>
    </>
  );
};

export default ExpandableChips;
